import * as React from "react";
import { CacheContext } from "../../../../contexts/CacheContext";
import { defaultOffices } from "../../../../store/cache/location";
import { useToasts } from "react-toast-notifications";
import {
    ClinicianOfficeWindowRequests,
    DateFormats,
    IClinician,
    OfficeRequestStatus,
} from "intake_shared/src/types";
import { AclContext } from "../../../../contexts/AclContext";
import store from "../../../../store";
import Table, { TablePagination } from "../../bits/Table/Table";
import {
    addSpaceToString,
    sortStringsWithNumberEndings,
    toTitleCase,
} from "../../../../utils/utils";
import { Dropdown } from "../../bits/Dropdown/Dropdown";
import ChevronDownIcon from "../../icons/ChevronDown";
import { Button } from "../../bits/Button/Button";
import { Badge } from "../../bits/Badge/Badge";
import { OFFICE_REQUEST_STATUS_COLORS } from "../../../../constants";
import moment from "moment";
import { TableHeader } from "../../../../types";
import EmptyListState from "../../bits/EmptyListState/EmptyListState";
import styles from "./RequestOfficeWindow.module.scss";

import FilterButton from "../../bits/FilterButton/FilterButton";
import { uniq } from "lodash";
import RefreshIcon from "../../icons/Refresh";
import RequestOfficeForm from "./RequestOfficeForm/RequestOfficeForm";
import Loader from "../../bits/Loader/Loader";
import { Tabs } from "../../bits/Tabs/Tabs";
import ClinicianDeleteRequests from "./ClinicianDeleteRequests/ClinicianDeleteRequests";

export default function RequestOfficeWindow() {
    const { offices: availableOffices = defaultOffices } = React.useContext(
        CacheContext
    ) || {
        offices: defaultOffices,
    };
    const { addToast } = useToasts();
    const [currentPage, setCurrentPage] = React.useState(1);
    const pageLimit = 50;
    const [selectedLocation, setSelectedLocation] = React.useState("");
    const [selectedOfficeNumber, setSelectedOfficeNumber] = React.useState("");
    const [selectedStatus, setSelectedStatus] = React.useState("");
    const [refreshKey, setRefreshKey] = React.useState(0);
    const [loggedInClinicianData, setLoggedInClinicianData] =
        React.useState<IClinician>();
    const [clinicianProfileLoading, setClinicianProfileLoading] =
        React.useState(true);
    const loggedInUserEmail = React.useContext(AclContext)?.authUser.user?.email;
    const [requestOfficeDetails, setRequestOfficeDetails] = React.useState({
        show: false,
    });
    const [nextButtonDisabled, setNextButtonDisabled] = React.useState(false);
    const [officeWindowRequests, setOfficeWindowRequests] =
        React.useState<ClinicianOfficeWindowRequests[]>();
    const [loading, setLoading] = React.useState(false);

    const getClinicianOfficeRequests = async () => {
        setLoading(true);
        try {
            const requests = await store.findOfficeWindowRequestsByClinicianEmail(
                loggedInUserEmail as string,
                currentPage,
                pageLimit,
                {
                    location: selectedLocation,
                    office: selectedOfficeNumber,
                    status: selectedStatus,
                    categories: ["request_list", "wait_list"],
                }
            );
            setOfficeWindowRequests(requests);
            setNextButtonDisabled(requests.length < pageLimit);
            setLoading(false);
        } catch (error) {
            setLoading(false);
            console.error("An error occurred:", error);
            addToast("An error occurred while fetching office window requests.", {
                appearance: "error",
                autoDismiss: false,
            });
        }
    };

    const getLoggedInClinicianDetails = async () => {
        try {
            const request = await store.getClinicianByEmail(
                loggedInUserEmail as string
            );
            setLoggedInClinicianData(request);
        } finally {
            setClinicianProfileLoading(false);
        }
    };

    React.useEffect(() => {
        getLoggedInClinicianDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        getClinicianOfficeRequests();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        requestOfficeDetails.show,
        refreshKey,
        currentPage,
        selectedLocation,
        selectedOfficeNumber,
        selectedStatus,
    ]);

    const deleteOfficeWindowRequests = async (officeWindowId: string) => {
        const deleted = await store.deleteOfficeWindowRequests(officeWindowId);
        if (deleted) {
            addToast("Office window request deleted successfully", {
                appearance: "success",
            });
            setRefreshKey((prevKey) => prevKey + 1);
        }
    };

    const columns = React.useMemo(() => {
        const headers: TableHeader[] = [
            {
                Header: "DATE OF REQUEST",
                id: "request_date",
                accessor: (row: ClinicianOfficeWindowRequests) => (
                    <p className="caseLdClName">{`${moment(row.request_date).format(
                        DateFormats.DAY_YEAR_HOUR_MIN_FORMAT
                    )}`}</p>
                ),
            },
            {
                Header: "REQUESTED DAY",
                id: "request_day",
                accessor: (row: ClinicianOfficeWindowRequests) => (
                    <p className="caseLdClName">{`${moment(row.start_date).format(
                        DateFormats.DAY
                    )}`}</p>
                ),
            },
            {
                Header: "REQUESTED TIME",
                id: "request_time",
                accessor: (row: ClinicianOfficeWindowRequests) => (
                    <p className="caseLdClName">{`${moment(row.start_date).format(
                        DateFormats.HOUR_MIN_FORMAT
                    )} - ${moment(row.end_date).format(
                        DateFormats.HOUR_MIN_FORMAT
                    )}`}</p>
                ),
            },

            {
                Header: "LOCATION",
                id: "location",
                accessor: (row: ClinicianOfficeWindowRequests) => (
                    <p>{row.location || "--"}</p>
                ),
            },
            {
                Header: "OFFICE",
                id: "office",
                accessor: (row: ClinicianOfficeWindowRequests) => (
                    <p>{row.office || "--"}</p>
                ),
            },
            {
                Header: "REOCCURRENCE",
                id: "recurrence",
                accessor: (row: ClinicianOfficeWindowRequests) => (
                    <p>{row.recurrence || "--"}</p>
                ),
            },
            {
                Header: "DELIVERY OPTION",
                id: "delivery_option",
                accessor: (row: ClinicianOfficeWindowRequests) => (
                    <p>
                        {toTitleCase(row.delivery_option)?.replace("_", " ") || "--"}
                    </p>
                ),
            },

            {
                Header: "STATUS",
                id: "status",
                accessor: (row: ClinicianOfficeWindowRequests) => (
                    <span>
                        <Badge
                            content={addSpaceToString(row.status || "")}
                            bgColor={OFFICE_REQUEST_STATUS_COLORS[row.status || ""]}
                            textColor="var(--color-white)"
                        />
                    </span>
                ),
            },
            {
                Header: "COMMENTS",
                id: "reason",
                accessor: (row: ClinicianOfficeWindowRequests) => {
                    return (
                        <div className="break_word">
                            {row?.rejection_reason ||
                                row?.approve_with_changes_reason ||
                                row.reason_for_holding ||
                                "--"}
                        </div>
                    );
                },
            },
            {
                Header: "Actions",
                id: "actions",
                accessor: (row: ClinicianOfficeWindowRequests) => {
                    return (
                        <Dropdown>
                            <Dropdown.Trigger>
                                <Button
                                    iconRight={
                                        <ChevronDownIcon stroke="var(--color-white)" />
                                    }
                                >
                                    Actions
                                </Button>
                            </Dropdown.Trigger>
                            <Dropdown.Content align="end" width="120px">
                                <Dropdown.Item
                                    onClick={() =>
                                        deleteOfficeWindowRequests(
                                            row.office_window_request_id as string
                                        )
                                    }
                                >
                                    Delete Request
                                </Dropdown.Item>
                            </Dropdown.Content>
                        </Dropdown>
                    );
                },
            },
        ];
        return headers;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getLocationLabel = (location: string) => {
        return location || "Select location";
    };

    const getOfficeLabel = (office: string) => {
        return office || "Select office";
    };

    const getStatusLabel = (status: string) => {
        return addSpaceToString(status) || "Select status";
    };

    const handleResetFilters = () => {
        setSelectedLocation("");
        setSelectedStatus("");
        setSelectedOfficeNumber("");
        setCurrentPage(1);
    };

    const handleLocationChange = (location: string) => {
        setSelectedLocation(location);
        setSelectedOfficeNumber("");
        setCurrentPage(1);
    };

    const handleOfficeChange = (office: string) => {
        setSelectedOfficeNumber(office);
        setCurrentPage(1);
    };

    const handleStatusChange = (status: string) => {
        setSelectedStatus(status);
        setCurrentPage(1);
    };

    const handleFirstPageClick = () => setCurrentPage(1);
    const handlePrevClick = () => {
        if (currentPage > 1) setCurrentPage(currentPage - 1);
    };
    const handleNextClick = () => setCurrentPage(currentPage + 1);
    const handleRefreshClick = () => setRefreshKey((prev) => prev + 1);
    const handleMakeRequestClick = () =>
        setRequestOfficeDetails({ ...requestOfficeDetails, show: true });

    const tableFooter = (
        <TablePagination
            currentPage={currentPage}
            onFirstPage={handleFirstPageClick}
            onPrevPage={handlePrevClick}
            onNextPage={handleNextClick}
            isNextDisabled={nextButtonDisabled}
            showPrevButton={currentPage > 1}
        />
    );

    const officeWindowRequestsTab = (
        <>
            <Loader show={loading} />
            <div className={styles.filters}>
                <div className={styles.filtersLeft}>
                    <Dropdown>
                        <Dropdown.Trigger>
                            <FilterButton
                                text={getLocationLabel(selectedLocation)}
                            />
                        </Dropdown.Trigger>

                        <Dropdown.Content width="170px">
                            <Dropdown.Item
                                closeMenuOnSelect
                                isSelected={selectedLocation === ""}
                                onClick={() => handleLocationChange("")}
                            >
                                Show all
                            </Dropdown.Item>
                            {Object.keys(availableOffices).map((k: string) => (
                                <Dropdown.Item
                                    key={k}
                                    closeMenuOnSelect
                                    isSelected={selectedLocation === k}
                                    onClick={() => handleLocationChange(k)}
                                >
                                    {k}
                                </Dropdown.Item>
                            ))}
                        </Dropdown.Content>
                    </Dropdown>
                    <Dropdown>
                        <Dropdown.Trigger>
                            <FilterButton
                                text={getOfficeLabel(selectedOfficeNumber)}
                            />
                        </Dropdown.Trigger>
                        <Dropdown.Content>
                            <Dropdown.Item
                                closeMenuOnSelect
                                isSelected={selectedOfficeNumber === ""}
                                onClick={() => handleOfficeChange("")}
                            >
                                Show all
                            </Dropdown.Item>
                            {sortStringsWithNumberEndings(
                                uniq(availableOffices[selectedLocation] || [])
                            ).map((r) => (
                                <Dropdown.Item
                                    key={r}
                                    onClick={() => handleOfficeChange(r)}
                                    isSelected={selectedOfficeNumber === r}
                                >
                                    {r}
                                </Dropdown.Item>
                            ))}
                        </Dropdown.Content>
                    </Dropdown>
                    <Dropdown>
                        <Dropdown.Trigger>
                            <FilterButton text={getStatusLabel(selectedStatus)} />
                        </Dropdown.Trigger>
                        <Dropdown.Content width="190px">
                            <Dropdown.Item
                                closeMenuOnSelect
                                isSelected={selectedStatus === ""}
                                onClick={() => handleStatusChange("")}
                            >
                                Show all
                            </Dropdown.Item>
                            {Object.values(OfficeRequestStatus).map((s) => (
                                <Dropdown.Item
                                    key={`status-${s}`}
                                    closeMenuOnSelect
                                    isSelected={selectedStatus === s}
                                    onClick={() => handleStatusChange(s)}
                                >
                                    {addSpaceToString(s)}
                                </Dropdown.Item>
                            ))}
                        </Dropdown.Content>
                    </Dropdown>
                    <Button onClick={handleRefreshClick}>
                        <RefreshIcon />
                    </Button>
                    <Button
                        variant="normal"
                        className={styles.resetFiltersButton}
                        onClick={handleResetFilters}
                    >
                        Reset Filters
                    </Button>
                </div>
                {!clinicianProfileLoading && loggedInClinicianData && (
                    <Button onClick={handleMakeRequestClick}>
                        Make an office window request
                    </Button>
                )}
            </div>
            {officeWindowRequests && (
                <div className={styles.requestOfficeWindow}>
                    <RequestOfficeForm
                        requestOfficeDetails={{
                            ...requestOfficeDetails,
                            onHide: () => {
                                setRequestOfficeDetails({
                                    ...requestOfficeDetails,
                                    show: false,
                                });
                            },
                        }}
                        loggedInClinicianData={loggedInClinicianData}
                        clinicianProfileLoading={clinicianProfileLoading}
                        setRequestOfficeDetails={setRequestOfficeDetails}
                        viewAs="clinician"
                        setRefreshKey={setRefreshKey}
                        refreshKey={refreshKey}
                    />
                    {officeWindowRequests?.length === 0 && (
                        <EmptyListState emptyMessage="No office window requests found" />
                    )}
                    {officeWindowRequests?.length > 0 && (
                        <Table
                            columns={columns}
                            data={officeWindowRequests}
                            allowSelect={false}
                            footer={
                                currentPage > 1 || !nextButtonDisabled
                                    ? tableFooter
                                    : null
                            }
                        />
                    )}
                </div>
            )}
        </>
    );

    const tabItems = [
        {
            key: "office-window-requests",
            label: "Office Window Requests",
            children: officeWindowRequestsTab,
        },
        {
            key: "deletion-requests",
            label: "Deletion Requests",
            children: <ClinicianDeleteRequests />,
        },
    ];

    return (
        <div className="v2-page">
            <Tabs items={tabItems} className={styles.tabs} />
        </div>
    );
}
